import { useEffect, useMemo, useState } from "react";
import { EventModel } from "../models/EventModel";
import { DateUtil } from "../utils/DateUtil";

type CacheEntry = {
    events: EventModel[];
    ts: number;
};

type Filters = {
    categories: string;
    free: string;
    period: string;
    time: string;
    searchQuery: string;
    city: string;
    venue: string;
    latitude?: string;
    longitude?: string;
};

const eventsCache = new Map<string, CacheEntry>();

export function useEvents(filters: Filters, options?: { maxAgeMs?: number }) {

    const maxAgeMs = options?.maxAgeMs ?? (60000 * 60); // 1h

    const key = useMemo(() => {
        return JSON.stringify({
            ...filters,
            categories: [...filters.categories].sort(),
            latitude: filters.latitude ?? null,
            longitude: filters.longitude ?? null,
            searchQuery: (filters.searchQuery ?? "").trim(),
        });
    }, [
        filters.categories,
        filters.free,
        filters.period,
        filters.time,
        filters.searchQuery,
        filters.city,
        filters.venue,
        filters.latitude,
        filters.longitude,
    ]);

    const cached = eventsCache.get(key);
    const [events, setEvents] = useState<EventModel[]>(cached?.events ?? []);
    const [loading, setLoading] = useState<boolean>(!cached);

    useEffect(() => {
        const now = Date.now();
        const cacheHit = eventsCache.get(key);
        const fresh = cacheHit && now - cacheHit.ts < maxAgeMs;

        if (fresh) {
            setEvents(cacheHit!.events);
            setLoading(false);
            return;
        }

        fetchEvents(filters)
    }, [key]);

    const buildQueryParams = (filters: Filters): URLSearchParams => {

        let startDate = "";
        let endDate = "";

        if (filters.period == "thisWeek") {
            const weekDays = DateUtil.getWeekDays();
            startDate = weekDays.monday;
            endDate = weekDays.sunday;
        }
        else if (filters.period == "nextWeek") {
            const weekDays = DateUtil.getWeekDays(true);
            startDate = weekDays.monday;
            endDate = weekDays.sunday;
        }
        else if (parseInt(filters.period) >= 0 && parseInt(filters.period) < 12) {
            const monthDays = DateUtil.getFirstAndLastDayOfMonth(parseInt(filters.period));
            startDate = monthDays.firstDay;
            endDate = monthDays.lastDay;
        }
        else if (DateUtil.isValidDateString(filters.period)) {
            startDate = filters.period;
        }

        const p = new URLSearchParams();
        p.set("category", filters.categories);
        p.set("timezone", "America/Toronto");
        p.set("date", startDate);
        if (filters.searchQuery) { p.set("searchQuery", filters.searchQuery); }
        if (filters.city) { p.set("city", filters.city); }
        if (endDate) { p.set("endDate", endDate); }
        if (filters.time) { p.set("time", filters.time); }
        if (filters.venue) { p.set("venue", filters.venue); }
        if (filters.free == '1') { p.set("free", '1'); }
        if (filters.latitude && filters.longitude) {
            p.set("lat", "46.81389902358164");
            p.set("long", "-71.21664211898582");
        }
        return p;

    };

    const fetchEvents = async (filters: Filters) => {
        setLoading(true);
        return new Promise<void>(async (resolve, reject) => {
            try {

                const queryParams = buildQueryParams(filters);
                if(queryParams.get("date")) {
                    let fetchUrl = `/api/events?${queryParams.toString()}`;
                
                    const response = await fetch(fetchUrl);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data: EventModel[] = await response.json();
                    eventsCache.set(key, { events: data, ts: Date.now() });
                    setEvents(data);

                    setLoading(false);
                    resolve();

                }
            } catch (error) {
                console.error('Error fetching events:', error);
                setLoading(false);
                reject();
            }
        });
    }

    return {
        events,
        loading
    };

}
